/**
 * Airport detection + per-airport waiting times.
 *
 * Replaces the old `constants/booking` AIRPORTS table and the haversine matcher
 * in `hooks/useAirportDetector` — a HARDCODED list of Canadian airports with
 * per-airport radii. The list now comes from `/config/airports`
 * (services/airportService.js), cached in react-query and read synchronously
 * here — so adding an airport, moving its coordinates, or changing its suggested
 * waiting times is a backend job, not a redeploy.
 *
 * IMPORTANT: every helper below reads the react-query cache SYNCHRONOUSLY and
 * returns "not an airport" while the list is still in flight. The list is
 * therefore fetched at app root (AppConfigLoader in App.jsx) before any route
 * mounts, the booking modal is gated on the same query, and the edit/confirm
 * flows await `ensureAirports()` before they derive a trip direction.
 *
 * `isNearAirport` keeps its old contract: the airport object on a hit, `null`
 * otherwise (callers do `!!x` / `Boolean(x)` and read `.code`).
 */

import { storage, STORAGE_KEYS } from '@/utils/storage';
import { getCachedAirports } from '@/hooks/useAirports.js';
import { BOOKING_CONSTRAINTS } from '@/constants/booking';

/* ================================
   RADIUS
================================ */

/**
 * How close a point has to be to count as "at the airport".
 *
 * The catchment radius is the BACKEND'S call — `location.radius_meters`, on the
 * airport or on an individual terminal. No default circle is guessed here: an
 * invented radius is what decides whether a downtown pickup is priced as an
 * airport trip, and that belongs with the airport record, not in the bundle.
 *
 * Until the field ships, a location matches only when it IS the airport (or one
 * of its terminals) — i.e. the passenger picked the same Google place the admin
 * pinned. EXACT_MATCH_METERS is only a tolerance for coordinate rounding
 * (Google returns 7–8 decimals, storage round-trips can shave a few), NOT a
 * catchment area.
 */
const EXACT_MATCH_METERS = 50;

/* ================================
   UTILS
================================ */

const toRad = (v) => (v * Math.PI) / 180;

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const getDistanceMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) ** 2;

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Backend radius for an airport or a terminal, else the rounding tolerance. */
const radiusFor = (location) => num(location?.radius_meters) ?? EXACT_MATCH_METERS;

/**
 * Flatten the API shape into what callers expect: the airport as sent, plus the
 * flat lat/lng/city/country the old detector exposed (several call sites read
 * `.code` / `.name`, and the flight-match check compares `.code` against the
 * AeroDataBox IATA).
 *
 * `terminal` is the terminal record the point actually landed on, or null when the
 * match came from the airport pin itself. It is what feeds `airport_terminal_id` on
 * the booking payload — terminal-aware features (which door the driver meets you at)
 * can't engage without it.
 */
const normalize = (airport, distanceMeters, terminal = null) => ({
    ...airport,
    lat: num(airport?.location?.lat),
    lng: num(airport?.location?.lng),
    city: airport?.location?.city || '',
    country: airport?.location?.country || '',
    terminal: terminal || null,
    distanceMeters: Math.round(distanceMeters),
});

/**
 * The airports we match against: everything the API returned minus the ones the
 * admin deactivated (the endpoint returns those too — filtering is our job).
 */
export function getAirports() {
    return getCachedAirports().filter((a) => a && a.is_active !== false);
}

/* ================================
   DETECTION
================================ */

/**
 * Is this coordinate at an airport?
 *
 * Matches the airport's own location OR any of its terminals — each against its
 * own `radius_meters`, or against EXACT_MATCH_METERS while the backend doesn't
 * send one. Terminals matter because Google returns the terminal as its own
 * place ("YYZ Terminal 1"), kilometres from the airport pin. When two airports
 * match, the CLOSEST wins.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {object|null} the airport (+ distanceMeters), or null
 */
export function isNearAirport(lat, lng) {
    const la = num(lat);
    const ln = num(lng);
    if (la === null || ln === null) return null;

    let best = null;

    for (const airport of getAirports()) {
        const aLat = num(airport?.location?.lat);
        const aLng = num(airport?.location?.lng);

        let distance = null;
        // Which terminal produced the winning distance, if a terminal did. Stays null
        // when the airport's own pin is the closest thing the point matched.
        let matchedTerminal = null;

        if (aLat !== null && aLng !== null) {
            const d = getDistanceMeters(la, ln, aLat, aLng);
            if (d <= radiusFor(airport.location)) distance = d;
        }

        // A terminal hit counts even when the airport pin is out of range.
        if (Array.isArray(airport?.terminals)) {
            for (const terminal of airport.terminals) {
                const tLat = num(terminal?.location?.lat);
                const tLng = num(terminal?.location?.lng);
                if (tLat === null || tLng === null) continue;
                const d = getDistanceMeters(la, ln, tLat, tLng);
                if (d <= radiusFor(terminal.location) && (distance === null || d < distance)) {
                    distance = d;
                    matchedTerminal = terminal;
                }
            }
        }

        if (distance !== null && (best === null || distance < best.distance)) {
            best = { airport, distance, terminal: matchedTerminal };
        }
    }

    return best ? normalize(best.airport, best.distance, best.terminal) : null;
}

/** The stored destinations: [pickup, dropoff, ...stopovers] — dropoff is index 1. */
const storedDestinations = () => storage.get(STORAGE_KEYS.DESTINATIONS, []) || [];

const airportAt = (dest) =>
    dest?.lat && dest?.lng ? isNearAirport(dest.lat, dest.lng) : null;

/** The pickup airport, or null. `trip_direction === 'from_airport'`. */
export function getFromAirport() {
    return airportAt(storedDestinations()[0]);
}

/**
 * The dropoff airport, or null. `trip_direction === 'to_airport'`.
 *
 * `destinations[1]`, NOT the last element: the array is
 * `[pickup, dropoff, ...stopovers]`, so reading the last element misses the
 * dropoff on any trip that has a stopover.
 */
export function getToAirport() {
    return airportAt(storedDestinations()[1]);
}

/**
 * The airport this trip is tied to, plus which end it sits on. Pickup wins over
 * dropoff — the same precedence tripService uses to pick trip_direction.
 *
 * @returns {{airport: object, fromAirport: boolean}|null}
 */
export function getTripAirport() {
    const pickup = getFromAirport();
    if (pickup) return { airport: pickup, fromAirport: true };
    const dropoff = getToAirport();
    if (dropoff) return { airport: dropoff, fromAirport: false };
    return null;
}

/**
 * The airport ids the booking payload carries: `airport_id` and, when the passenger
 * picked a specific terminal, `airport_terminal_id`.
 *
 * Both come off the SAME record the direction was decided from, so they can never
 * disagree with `trip_direction`: pickup airport wins over dropoff, exactly as in
 * getTripAirport(). Derived from the coordinates rather than carried on the place
 * object, so a passenger who typed the airport into Google (instead of picking it off
 * our own suggestion list) is identified just the same.
 *
 * Both null on a city-to-city trip — omit the fields entirely in that case.
 *
 * @returns {{airport_id: number|string|null, airport_terminal_id: number|string|null}}
 */
export function getTripAirportIds() {
    const trip = getTripAirport();
    return {
        airport_id: trip?.airport?.id ?? null,
        airport_terminal_id: trip?.airport?.terminal?.id ?? null,
    };
}

/**
 * Same two ids, but from a pair of locations passed in rather than from the booking
 * flow's storage — for /edit and /confirm, which work off a trip resource and hold
 * their destinations in component state.
 *
 * Pickup wins over dropoff, matching directionFor() in useEditTrip / useConfirmTrip, so
 * the ids always describe the same end of the trip as the `trip_direction` sent with
 * them. Feed it the SAME dests the payload carries (the postal-code-backfilled ones),
 * never the render-time state, or an in-flight location edit can put the two out of step.
 *
 * @param {{lat: number, lng: number}} origin
 * @param {{lat: number, lng: number}} destination
 * @returns {{airport_id: number|string|null, airport_terminal_id: number|string|null}}
 */
export function getAirportIdsFor(origin, destination) {
    const at = (point) => (point?.lat && point?.lng ? isNearAirport(point.lat, point.lng) : null);
    const airport = at(origin) || at(destination);
    return {
        airport_id: airport?.id ?? null,
        airport_terminal_id: airport?.terminal?.id ?? null,
    };
}

export function isFromAirport() {
    return !!getFromAirport();
}

export function isToAirport() {
    return !!getToAirport();
}

/**
 * A from-airport ride cannot be booked without a flight number.
 *
 * The API enforces this as of the Aug-2026 change (422 on create, on confirm, and on
 * any edit that would clear the field): without a flight there is nothing to track, so
 * the driver has no arrival to meet and the pickup cushion never engages. To-airport
 * and city trips are unaffected — a typed departure time is enough there.
 *
 * Booking-flow only: it reads the flow's stored destinations. The edit/confirm pages
 * work off a trip resource instead and do their own check against that.
 *
 * @returns {boolean} true when the trip needs a flight number and hasn't got one
 */
export function isMissingRequiredFlightNumber() {
    return isFromAirport() && !storage.get(STORAGE_KEYS.FLIGHT_NUMBER);
}

/** One sentence, shown wherever the check above blocks a step. */
export const FLIGHT_NUMBER_REQUIRED_MESSAGE =
    "Add your flight number — we track your arrival so the driver meets the flight you're actually on.";

export function isAirportTrip() {
    return isFromAirport() || isToAirport();
}

/* ================================
   WAITING TIMES
================================ */

/**
 * The suggested-waiting bucket for a leg: direction × flight scope.
 *
 *   from airport + domestic      → from_airport_local
 *   from airport + international → from_airport_international
 *   to airport   + domestic      → to_airport_local
 *   to airport   + international → to_airport_international
 *
 * "Local" means the other end of the flight is in the same country as the
 * airport, which is exactly what flightService.getFlightType() reports as
 * "Domestic".
 *
 * All four buckets are always present; an UNCONFIGURED one is `items: []` +
 * `total_minutes: 0` and is reported here as null so callers fall back to the
 * global /config/app cushion (see getAirportBufferMinutes).
 *
 * @param {object} airport            an airport from isNearAirport()
 * @param {object} opts
 * @param {boolean} opts.fromAirport  true = leaving the airport
 * @param {boolean} opts.international
 * @returns {{label: string, items: Array<{name: string, minutes: number}>, totalMinutes: number}|null}
 */
export function getWaitingBucket(airport, { fromAirport, international } = {}) {
    const key = `${fromAirport ? 'from' : 'to'}_airport_${international ? 'international' : 'local'}`;
    const bucket = airport?.waiting_times?.[key];
    if (!bucket) return null;

    const items = (Array.isArray(bucket.items) ? bucket.items : [])
        .filter((it) => it && it.name)
        .map((it) => ({ name: String(it.name), minutes: Math.max(0, Math.round(Number(it.minutes) || 0)) }));
    // Server-computed — never re-summed from `items`.
    const totalMinutes = Math.max(0, Math.round(Number(bucket.total_minutes) || 0));

    if (!items.length && !totalMinutes) return null;

    return { label: bucket.label || '', items, totalMinutes };
}

/**
 * Icon FILENAME (under assets/images) for a waiting_times item.
 *
 * The API sends a label and minutes, never an icon, so it is matched off the
 * label the admin typed — first rule wins, anything unrecognised gets a neutral
 * clock. Kept here, keyed by filename, so this is byte-identical to the plugin's
 * mapping and both portals show the same icon for the same row. Resolve it with
 * `waitingItemImage()` (sharedComponents/smartTime/waitingIcons).
 */
const WAITING_ITEM_ICONS = [
    [/immigration|customs|border|processing/i, 'security-bold.svg'],
    [/baggage|luggage|bag/i, 'bag-bold.svg'],
    [/traffic/i, 'performance.svg'],
    [/security|check.?in|buffer|arrival/i, 'shield-protected.svg'],
    [/drive|driving|road|car/i, 'bold-car.svg'],
    [/flight|depart|plane/i, 'plane-take-off.svg'],
];

export function getWaitingItemIcon(name) {
    const match = WAITING_ITEM_ICONS.find(([pattern]) => pattern.test(name || ''));
    return match ? match[1] : 'clock-bold.svg';
}

/**
 * The cushion the pickup recommendation is computed from, in minutes.
 *
 *   to_airport   → pickup = flight departure − travel time − cushion
 *   from_airport → pickup = flight landing   + cushion
 *
 * Per-airport `total_minutes` when the bucket is configured, else the global
 * booking.{to,from}_airport_extra_minutes from /config/app. The backend's
 * flight-delay auto-shift engine resolves the cushion the same way, so the time
 * we suggest and the time it shifts to after a delay agree — do NOT pad on top.
 */
export function getAirportBufferMinutes(airport, opts = {}) {
    const bucket = getWaitingBucket(airport, opts);
    if (bucket && bucket.totalMinutes > 0) return bucket.totalMinutes;
    return opts.fromAirport
        ? BOOKING_CONSTRAINTS.FROM_AIRPORT_BUFFER_MINUTES
        : BOOKING_CONSTRAINTS.TO_AIRPORT_BUFFER_MINUTES;
}

/**
 * The terminal-time figure for the "we recommend arriving ~X before departure"
 * note: the cushion MINUS its road allowance. A "predicted traffic" item pads
 * the journey so the terminal buffer survives heavy traffic — counting it would
 * claim 3 hr 20 min of check-in time when the airport asked for 3 hr.
 */
export function getArriveEarlyMinutes(bufferMinutes, items = []) {
    const traffic = (items || [])
        .filter((item) => /traffic/i.test(item?.name || ''))
        .reduce((sum, item) => sum + (Number(item?.minutes) || 0), 0);
    return Math.max(0, (Number(bufferMinutes) || 0) - traffic);
}
